import { useMemo } from "react";
import { useDefaultYear } from "@hooks/useDefaultYear";
import {
    TaxQuestion,
    TaxQuestionKey,
    taxQuestions,
} from "@common/taxSavingRules";
import { Taxonomy } from "@common/categories";
import { toUTCDateOnly } from "@common/helpers/date";

interface TransactionWithTaxInfo {
    date: Date;
    dateOnly?: string | null;
    category: { taxonomy: Taxonomy };
    taxQuestions: TaxQuestionKey[];
}

export function useApplicableTaxQuestions(
    transaction: TransactionWithTaxInfo | undefined,
): TaxQuestion[] {
    const defaultYear = useDefaultYear();

    return useMemo(() => {
        if (!transaction) {
            return [];
        }
        const taxQuestionsForTransaction =
            taxQuestions[
                Number(
                    (
                        transaction.dateOnly ?? toUTCDateOnly(transaction.date)
                    ).slice(0, 4),
                )
            ]?.[transaction.category.taxonomy] ??
            taxQuestions[defaultYear]?.[transaction.category.taxonomy];

        const taxQuestionsMap = new Map(
            taxQuestionsForTransaction?.map((q) => [q.key, q]) ?? [],
        );

        return transaction.taxQuestions
            .map((key) => taxQuestionsMap.get(key))
            .filter((q): q is TaxQuestion => q !== undefined);
    }, [transaction, defaultYear]);
}
